import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, X, Loader2, CheckCircle2, AlertTriangle, ShieldCheck, Download,
  Trash2, ChevronDown, ChevronUp, MapPin,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Input, Select, Textarea, Spinner, EmptyState, type Tone } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import {
  computeStage, availableBooms, boomOptions, bearingPressureKpa, bearingVerdict, fmtKg,
  type Stage, type ChartPoint,
} from "./liftPlanCalc";
import {
  LiftPlanPlanView, LiftPlanElevation, CapacityCurve, DEFAULT_CANVAS, normaliseCanvas,
  radiusOf, structurePos, COMPOUND_ID, newItem, type CanvasState, type PlacedItem,
} from "./LiftPlanCanvas";
import { exportLiftPlanPdf } from "./liftPlanPdf";

// =============================================================================
// Lift plans — T-436
// =============================================================================
// Same spine as site SWMS: project-linked, manager+ drafts, DIRECTOR approves,
// immutable snapshot on approval, branded PDF for the client.
//
// A lift plan is a sequence of STAGES (Borger's Park Ridge study runs a
// different boom length per pole section; Ramsamy's Andergrove set is four
// separate lifts). Each stage carries its own crane config, weights, radii and
// verdict — because the governing case is per-lift, not per-day.
// =============================================================================

const emptyStage = (n: number): Stage => ({
  name: `Lift ${n}`,
  piece_desc: "",
  piece_length_m: 0,
  built_height_m: 0,
  piece_weight_kg: 0,
  other_allowance_kg: 0,
  hook_kg: 200,
  rope_kg: 100,
  rigging: [],
  boom_length_m: 0,
  variant: "",
  pick_radius_m: 10,
  place_radius_m: 10,
  dual_lift: false,
});

// The brief is everything a client's HSE reviewer looks for before they even
// reach the numbers: where is it, what are we doing, who's craning, how hard is
// this lift. Without it the document reads like a calculation sheet rather than
// a lift study (James, 01/08).
export type PlanBrief = {
  site_address?: string;
  scope?: string;                  // scope of works, in the client's language
  lift_class?: string;             // basic / standard / complex / critical
  // Tender vs engaged changes what this document IS, and therefore what the
  // limitations panel should say (James, 02/08). At tender there's no
  // engagement and no PCBU duty for the works — it's an indicative method.
  purpose?: "tender" | "engaged";
  // Per-plan, not blanket: on a job with issued FCs the weights ARE certified.
  weights_basis?: "certified" | "indicative";
  weights_note?: string;           // where the indicative weights came from
  lift_date?: string;              // planned date
  duration?: string;
  crane_company?: string;
  crane_contact?: string;
  access?: string;                 // site access + hardstand
  emergency?: string;              // muster point, nearest hospital
  references?: string;             // AS 2550.1, client procedures, SWMS numbers
};

type PlanContent = {
  canvas: CanvasState;
  // ONE crane per lift plan (James, 01/08) — you hire a crane for the day, not
  // per lift. Each stage varies boom length, radius and height off that crane.
  crane_config_id: string | null;
  stages: Stage[];
  brief: PlanBrief;
  sequence: string[];              // numbered methodology / hold points
  personnel: { operator?: string; dogman?: string; riggers?: string; supervisor?: string };
  ground: { pads: { leg: string; load_t: number; length_m: number; width_m: number; tare_t: number }[]; assessment?: string };
  environment: { wind_limit_ms?: number | null; powerlines?: string; public_interface?: string };
  hazards: { hazard: string; control: string }[];
  notes?: string;
};

const emptyContent = (): PlanContent => ({
  canvas: { ...DEFAULT_CANVAS },
  crane_config_id: null,
  stages: [emptyStage(1)],
  brief: {
    lift_class: "Standard",
    references: "AS 2550.1 Cranes — Safe use · AS 1418.5 · Coretel SWMS",
  },
  sequence: [],
  personnel: {},
  ground: {
    pads: [
      { leg: "Front left", load_t: 0, length_m: 1.6, width_m: 1.2, tare_t: 1.0 },
      { leg: "Front right", load_t: 0, length_m: 1.6, width_m: 1.2, tare_t: 1.0 },
      { leg: "Rear left", load_t: 0, length_m: 1.6, width_m: 1.2, tare_t: 1.0 },
      { leg: "Rear right", load_t: 0, length_m: 1.6, width_m: 1.2, tare_t: 1.0 },
    ],
  },
  environment: {},
  hazards: [],
});

export default function LiftPlanScreen() {
  const { liftPlanId } = useParams<{ liftPlanId: string }>();
  return liftPlanId ? <LiftPlanEditor id={liftPlanId}/> : <LiftPlanList/>;
}

// -----------------------------------------------------------------------------
function LiftPlanList() {
  const user = useRequireAuth();
  const canEdit = hasRole(user, "manager");
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["lift_plans"],
    queryFn: async () => {
      const { data, error } = await supabase.from("lift_plans")
        .select("lift_plan_id, plan_number, title, status, version, lift_type, structure_type, approved_at, projects:project_id(project_code, sites:site_id(site_name)), bids:bid_id(bid_number, bid_title, sites:site_id(site_name))")
        .neq("status", "superseded")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">HSE</div>
          <h1 className="text-2xl font-semibold tracking-tight">Lift plans</h1>
          <p className="text-text-muted text-[13px] mt-1 max-w-[560px]">
            Site-specific lift planning off real crane charts — weights in, capacity and
            utilisation out, plan and elevation drawn, branded PDF for the client.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/hse/cranes"><Button variant="secondary">Crane library</Button></Link>
          {canEdit && <Button icon={Plus} onClick={() => setAdding(true)}>New lift plan</Button>}
        </div>
      </div>

      {isLoading && <div className="flex justify-center py-10"><Spinner size={18}/></div>}
      {!isLoading && plans.length === 0 && (
        <EmptyState icon={MapPin} title="No lift plans yet"
          description="Start one against a project — you'll need a verified crane chart in the library first."/>
      )}

      {plans.length > 0 && (
        <Card>
          <div className="divide-y divide-line/40">
            {plans.map(p => (
              <button key={p.lift_plan_id} type="button"
                onClick={() => navigate(`/hse/lift-plans/${p.lift_plan_id}`)}
                className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap hover:bg-ink-raised/40 transition">
                <span className="font-mono text-[12px] text-accent w-[104px]">{p.plan_number}</span>
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[13px]">{p.title}</div>
                  <div className="text-[11px] text-text-muted">
                    {p.bids
                      ? `${p.bids.bid_number}${p.bids.sites?.site_name ? ` · ${p.bids.sites.site_name}` : ""}`
                      : `${p.projects?.project_code ?? ""}${p.projects?.sites?.site_name ? ` · ${p.projects.sites.site_name}` : ""}`}
                  </div>
                </div>
                {p.bids && <Pill label="quoting" tone="notice" small/>}
                <Pill label={p.structure_type} tone="default" small/>
                {p.status === "approved"
                  ? <Pill label={`approved v${p.version}`} tone="ok" small/>
                  : <Pill label="DRAFT — NOT FOR USE" tone="warning" small/>}
              </button>
            ))}
          </div>
        </Card>
      )}

      {adding && <NewPlanModal onClose={() => setAdding(false)}
        onCreated={id => { setAdding(false); navigate(`/hse/lift-plans/${id}`); }}/>}
    </div>
  );
}

function NewPlanModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  // A lift plan hangs off a BID while quoting, or a PROJECT once it's won.
  const [against, setAgainst] = useState<"project" | "bid">("project");
  const [projectId, setProjectId] = useState("");
  const [bidId, setBidId] = useState("");
  const [title, setTitle] = useState("");
  const [structure, setStructure] = useState("monopole");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: projects = [] } = useQuery({
    queryKey: ["lift_plan_projects"],
    queryFn: async () => {
      const { data } = await supabase.from("projects")
        .select("project_id, project_code, project_name, sites:site_id(site_name)")
        .in("status", ["scheduled", "won", "mobilising", "live", "on_hold", "acceptance_pending"])
        .order("project_code", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  const { data: bids = [] } = useQuery({
    queryKey: ["lift_plan_bids"],
    enabled: against === "bid",
    queryFn: async () => {
      const { data } = await supabase.from("bids")
        .select("bid_id, bid_number, bid_title, sites:site_id(site_name)")
        .in("status", ["open", "responded", "won"])
        .order("bid_number", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  const chosen = against === "bid"
    ? bids.find(b => b.bid_id === bidId)
    : projects.find(p => p.project_id === projectId);
  const ready = against === "bid" ? !!bidId : !!projectId;

  async function create() {
    setBusy(true); setErr(null);
    const content = emptyContent();
    content.canvas.structure = structure as any;
    const fallback = (chosen as any)?.sites?.site_name
      ?? (chosen as any)?.bid_title ?? (chosen as any)?.project_name ?? "";
    const { data, error } = await supabase.schema("app").rpc("save_lift_plan", {
      p_lift_plan_id: null,
      p_project_id: against === "project" ? projectId : null,
      p_bid_id: against === "bid" ? bidId : null,
      p_title: title.trim() || `Lift plan — ${fallback}`,
      p_content: content,
      p_lift_type: "single",
      p_structure_type: structure,
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onCreated(data as string);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-[10vh]" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-[520px]" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">New lift plan</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}

          <div className="flex gap-2">
            {(["project", "bid"] as const).map(k => (
              <button key={k} type="button" onClick={() => setAgainst(k)}
                className={`flex-1 text-[12px] px-3 py-2 rounded-md border transition ${against === k
                  ? "border-accent bg-accent/10 text-accent" : "border-line text-text-muted hover:text-text"}`}>
                {k === "project" ? "Against a project" : "Against a BID (quoting)"}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-text-muted -mt-1">
            {against === "bid"
              ? "Size the crane while you're pricing. If the bid is won, the plan moves across to the project."
              : "For work already won and scheduled."}
          </div>

          {against === "project" ? (
            <label className="block text-[12px] text-text-muted">Project *
              <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
                <option value="">Pick…</option>
                {projects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project_code} · {p.sites?.site_name ?? p.project_name}
                  </option>
                ))}
              </Select>
            </label>
          ) : (
            <label className="block text-[12px] text-text-muted">BID *
              <Select value={bidId} onChange={e => setBidId(e.target.value)}>
                <option value="">Pick…</option>
                {bids.map(b => (
                  <option key={b.bid_id} value={b.bid_id}>
                    {b.bid_number} · {b.sites?.site_name ?? b.bid_title}
                  </option>
                ))}
              </Select>
            </label>
          )}
          <label className="block text-[12px] text-text-muted">Structure
            <Select value={structure} onChange={e => setStructure(e.target.value)}>
              <option value="monopole">Monopole</option>
              <option value="lattice">Lattice tower</option>
              <option value="headframe">Headframe</option>
              <option value="other">Other</option>
            </Select>
          </label>
          <label className="block text-[12px] text-text-muted">Title
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Lift and place 30m monopole"/>
          </label>
          <Button onClick={create} disabled={busy || !ready} className="w-full justify-center">
            {busy ? <Loader2 size={14} className="animate-spin"/> : "Create lift plan"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Editor
// -----------------------------------------------------------------------------
function LiftPlanEditor({ id }: { id: string }) {
  const user = useRequireAuth();
  const canEdit = hasRole(user, "manager");
  const isDirector = hasRole(user, "director");
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [content, setContent] = useState<PlanContent | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<null | "save" | "approve" | "pdf">(null);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [openStage, setOpenStage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: row, isLoading } = useQuery({
    queryKey: ["lift_plan", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("lift_plans")
        .select("*, projects:project_id(project_code, project_name, clients:client_id(legal_name), sites:site_id(site_name, address_line1, suburb, state, postcode, latitude, longitude)), bids:bid_id(bid_number, bid_title, clients:client_id(legal_name), sites:site_id(site_name, address_line1, suburb, state, postcode, latitude, longitude)), approver:approved_by_user_id(login_email, employees:employee_id(first_name, last_name))")
        .eq("lift_plan_id", id).single();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: configs = [] } = useQuery({
    queryKey: ["crane_configs_verified"],
    queryFn: async () => {
      const { data, error } = await supabase.from("crane_configs")
        .select("*, crane_models(make, model)")
        .eq("status", "verified").order("config_name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // One config per plan — plus any legacy per-stage ids so old plans still read.
  // MUST fall back to the saved row: `content` is null until the first edit, so
  // reading only from it meant a freshly-opened plan fetched no chart points and
  // the boom dropdown came up empty (James, 01/08).
  const usedConfigIds = useMemo(() => {
    const src: any = content ?? (row as any)?.content ?? {};
    const ids = [
      src.crane_config_id,
      ...((src.stages ?? []) as any[]).map(s => s?.crane_config_id),
    ].filter(Boolean) as string[];
    return Array.from(new Set(ids));
  }, [content, row]);

  const { data: points = [] } = useQuery({
    queryKey: ["chart_points", usedConfigIds.join(",")],
    enabled: usedConfigIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from("crane_chart_points")
        .select("crane_config_id, boom_length_m, radius_m, variant, capacity_kg")
        .in("crane_config_id", usedConfigIds);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  if (isLoading || !row) return <div className="py-20 flex justify-center"><Spinner/></div>;
  const seeded: PlanContent = { ...emptyContent(), ...(row.content ?? {}) };
  // normaliseCanvas backfills plans saved before drag/items existed.
  // The BID already knows the site — don't make James retype the address
  // (01/08). Only used to seed a blank field; anything typed always wins.
  const siteRow: any = row.bids?.sites ?? row.projects?.sites ?? null;
  const siteAddress = siteRow
    ? [siteRow.address_line1, siteRow.suburb, siteRow.state, siteRow.postcode].filter(Boolean).join(", ")
    : "";

  const c: PlanContent = content ?? {
    ...seeded,
    canvas: normaliseCanvas(seeded.canvas),
    brief: {
      ...emptyContent().brief,
      ...(seeded.brief ?? {}),
      site_address: seeded.brief?.site_address || siteAddress || "",
    },
    sequence: seeded.sequence ?? [],
    hazards: seeded.hazards ?? [],
  };

  const patch = (next: Partial<PlanContent>) => { setContent({ ...c, ...next }); setDirty(true); };
  const patchStage = (i: number, next: Partial<Stage>) => {
    const stages = [...c.stages]; stages[i] = { ...stages[i], ...next };
    patch({ stages });
  };
  const patchCanvas = (next: Partial<CanvasState>) => patch({ canvas: { ...c.canvas, ...next } });

  const cfgFor = (sid: string | null) => configs.find(x => x.crane_config_id === sid);
  const pointsFor = (sid: string | null): ChartPoint[] =>
    points.filter(p => p.crane_config_id === sid).map(p => ({
      boom_length_m: Number(p.boom_length_m), radius_m: Number(p.radius_m),
      variant: p.variant ?? "", capacity_kg: p.capacity_kg,
    }));

  // Plan-level crane, falling back to a legacy per-stage id on older plans.
  const planConfigId = c.crane_config_id ?? c.stages.find(s => s.crane_config_id)?.crane_config_id ?? null;
  const planConfig = cfgFor(planConfigId);
  const planPoints = pointsFor(planConfigId);
  const results = c.stages.map(s => computeStage(s, planPoints, planConfig?.main_winch_capacity_kg));
  const worst = results.reduce<"ok" | "caution" | "over" | "unknown">((acc, r) =>
    r.band === "over" ? "over" : acc === "over" ? acc : r.band === "unknown" ? "unknown"
      : acc === "unknown" ? acc : r.band === "caution" ? "caution" : acc, "ok");

  async function save() {
    setBusy("save"); setErr(null);
    const { error } = await supabase.schema("app").rpc("save_lift_plan", {
      p_lift_plan_id: id,
      p_project_id: row.project_id,
      p_bid_id: row.bid_id,
      p_title: row.title,
      p_content: c,
      p_lift_type: c.stages.some(s => s.dual_lift) ? "dual" : c.stages.length > 1 ? "multi" : "single",
      p_structure_type: c.canvas.structure,
    });
    setBusy(null);
    if (error) { setErr(error.message); return; }
    setDirty(false); setSaved(true); setTimeout(() => setSaved(false), 2200);
    qc.invalidateQueries({ queryKey: ["lift_plan", id] });
  }

  async function approve() {
    if (worst === "over") {
      window.alert("A stage is over capacity. Fix the configuration before approving.");
      return;
    }
    if (!window.confirm(
      "Approve this lift plan?\n\nYou're confirming the weights, radii and crane configuration have been checked against the chart, and that a competent person is satisfied with the method.",
    )) return;
    setBusy("approve"); setErr(null);
    if (dirty) await save();
    const { error } = await supabase.schema("app").rpc("approve_lift_plan", { p_lift_plan_id: id });
    setBusy(null);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["lift_plans"] });
    navigate("/hse/lift-plans");
  }

  async function pdf() {
    setBusy("pdf");
    try {
      const approverName = row.approver
        ? ([row.approver.employees?.first_name, row.approver.employees?.last_name].filter(Boolean).join(" ").trim() || row.approver.login_email)
        : null;
      await exportLiftPlanPdf({
        planNumber: row.plan_number,
        title: row.title,
        version: row.version || 0,
        status: dirty ? "draft" : row.status,
        projectCode: row.bids?.bid_number ?? row.projects?.project_code,
        siteName: row.bids?.sites?.site_name ?? row.projects?.sites?.site_name,
        clientName: row.bids?.clients?.legal_name ?? row.projects?.clients?.legal_name,
        approvedBy: approverName,
        approvedAt: row.approved_at,
        preparedBy: [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.login_email || null,
        content: c,
        stages: c.stages.map((s, i) => ({
          stage: s,
          result: results[i],
          config: planConfig,          // one crane per plan
        })),
        // One elevation per stage — v1 printed only whichever stage happened to
        // be open, so the client saw the base module and nothing else.
        elevationIds: c.stages.map((_, i) => `lp-elev-pdf-${i}`),
        planIds: c.stages.map((_, i) => `lp-plan-pdf-${i}`),
      });
    } catch (e: any) {
      window.alert(`PDF failed: ${e?.message ?? e}`);
    }
    setBusy(null);
  }

  const approved = row.status === "approved" && !dirty;
  const activeStage = c.stages[openStage] ?? c.stages[0];
  const activeResult = results[openStage] ?? results[0];

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto pb-32 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/hse/lift-plans" className="text-[12px] text-text-muted hover:text-text">← Lift plans</Link>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className="font-mono text-[13px] text-accent">{row.plan_number}</span>
            {approved
              ? <Pill label={`approved v${row.version}`} tone="ok"/>
              : <Pill label="DRAFT — NOT FOR USE" tone="warning"/>}
          </div>
          <h1 className="text-xl font-semibold tracking-tight mt-0.5">{row.title}</h1>
          <div className="text-[12px] text-text-muted mt-0.5">
            {row.bids
              ? <>{row.bids.bid_number} · {row.bids.sites?.site_name ?? row.bids.bid_title} <span className="text-accent">· quoting</span></>
              : <>{row.projects?.project_code} · {row.projects?.sites?.site_name ?? row.projects?.project_name}</>}
          </div>
        </div>
        <Button variant="secondary" icon={Download} onClick={() => void pdf()} disabled={busy != null}>
          {busy === "pdf" ? <Loader2 size={14} className="animate-spin"/> : `PDF${approved ? "" : " (draft)"}`}
        </Button>
      </div>

      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      {!approved && (
        <div className="bg-warn/10 border border-warn/40 rounded-md px-4 py-2.5 text-[12px] text-warn">
          <AlertTriangle size={12} className="inline mr-1.5 -mt-0.5"/>
          Draft — crews must not lift to this until a director approves it. The crane company's own
          lift study still governs when the crane is hired.
        </div>
      )}

      {/* One crane for the whole plan */}
      <Card>
        <div className="p-4">
          <label className="block text-[12px] text-text-muted">Crane for this lift plan
            <Select value={planConfigId ?? ""} disabled={!canEdit}
              onChange={e => patch({
                crane_config_id: e.target.value || null,
                // boom lengths belong to the chart — clear them on a crane change
                stages: c.stages.map(s => ({ ...s, crane_config_id: null, boom_length_m: 0 })),
              })}>
              <option value="">Pick a verified configuration…</option>
              {configs.map(cf => (
                <option key={cf.crane_config_id} value={cf.crane_config_id}>
                  {cf.crane_models?.make} {cf.crane_models?.model} — {cf.config_name}
                  {cf.chart_basis === "machine" ? "" : " (generic)"}
                </option>
              ))}
            </Select>
          </label>
          {configs.length === 0 && (
            <div className="text-[11px] text-warn mt-1.5">
              No verified charts yet — load and verify one in the <Link to="/hse/cranes" className="underline">crane library</Link> first.
            </div>
          )}
          {planConfig?.chart_basis === "generic" && (
            <div className="text-[11px] text-warn mt-1.5">
              Planning off a generic manufacturer chart — fine for quoting and sizing, but get the
              hired machine's own chart before the lift.
            </div>
          )}
          {planConfig && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Stat label="Counterweight" value={planConfig.counterweight_t != null ? `${planConfig.counterweight_t} t` : "—"}/>
              <Stat label="Outriggers" value={planConfig.outrigger_width_m ? `${planConfig.outrigger_width_m} × ${planConfig.outrigger_length_m} m` : "—"}/>
              <Stat label="Main winch" value={planConfig.main_winch_capacity_kg ? `${(planConfig.main_winch_capacity_kg / 1000).toFixed(1)} t` : "—"}/>
              <Stat label="Chart wind" value={planConfig.chart_wind_limit_ms ? `${planConfig.chart_wind_limit_ms} m/s` : "—"}/>
            </div>
          )}
        </div>
      </Card>

      {/* The brief. Without this the PDF reads like a calculation sheet rather
          than a lift study — it's the first thing a client's HSE reviewer looks
          for and none of it was being captured (James, 01/08). */}
      <Card>
        <div className="p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Job brief · goes on the cover</div>
          <label className="block text-[12px] text-text-muted">Scope of works
            <Textarea rows={3} value={c.brief.scope ?? ""} disabled={!canEdit}
              onChange={e => patch({ brief: { ...c.brief, scope: e.target.value } })}
              placeholder="Erect a 30 m Rocla concrete monopole in four sections and install the Optus headframe to 31 m AGL…"/>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Txt label="Site address" value={c.brief.site_address ?? ""} disabled={!canEdit}
              onChange={v => patch({ brief: { ...c.brief, site_address: v } })} placeholder="Lot 3, Coast Rd, Pottsville Beach NSW"/>
            <Txt label="Planned date" value={c.brief.lift_date ?? ""} disabled={!canEdit}
              onChange={v => patch({ brief: { ...c.brief, lift_date: v } })} placeholder="Week of 18 Aug 2026"/>
            <Txt label="Expected duration" value={c.brief.duration ?? ""} disabled={!canEdit}
              onChange={v => patch({ brief: { ...c.brief, duration: v } })} placeholder="1 day"/>
            <Txt label="Crane hire company" value={c.brief.crane_company ?? ""} disabled={!canEdit}
              onChange={v => patch({ brief: { ...c.brief, crane_company: v } })} placeholder="Borger Cranes"/>
            <Txt label="Crane contact" value={c.brief.crane_contact ?? ""} disabled={!canEdit}
              onChange={v => patch({ brief: { ...c.brief, crane_contact: v } })} placeholder="Name / mobile"/>
            <label className="block text-[12px] text-text-muted">Issued for
              <Select value={c.brief.purpose ?? "tender"} disabled={!canEdit}
                onChange={e => patch({ brief: { ...c.brief, purpose: e.target.value as any } })}>
                <option value="tender">Tender / proposal — not yet engaged</option>
                <option value="engaged">Engaged works — order received</option>
              </Select>
            </label>
            <label className="block text-[12px] text-text-muted">Lift classification
              <Select value={c.brief.lift_class ?? "Standard"} disabled={!canEdit}
                onChange={e => patch({ brief: { ...c.brief, lift_class: e.target.value } })}>
                <option>Basic</option><option>Standard</option><option>Complex</option><option>Critical</option>
              </Select>
            </label>
            <Txt label="Site access & hardstand" value={c.brief.access ?? ""} disabled={!canEdit}
              onChange={v => patch({ brief: { ...c.brief, access: v } })} placeholder="Sealed access off Coast Rd, compacted pad"/>
            <Txt label="Emergency / muster" value={c.brief.emergency ?? ""} disabled={!canEdit}
              onChange={v => patch({ brief: { ...c.brief, emergency: v } })} placeholder="Muster at gate; Tweed Valley Hospital 14 km"/>
            <label className="block text-[12px] text-text-muted">Piece weights are
              <Select value={c.brief.weights_basis ?? "certified"} disabled={!canEdit}
                onChange={e => patch({ brief: { ...c.brief, weights_basis: e.target.value as any } })}>
                <option value="certified">Certified — from issued FC drawings</option>
                <option value="indicative">Indicative — FCs not yet issued</option>
              </Select>
            </label>
            {c.brief.weights_basis === "indicative" && (
              <Txt label="Where the weights came from" value={c.brief.weights_note ?? ""} disabled={!canEdit}
                onChange={v => patch({ brief: { ...c.brief, weights_note: v } })}
                placeholder="Taken from previously supplied Rocla SR5 poles of the same type."/>
            )}
            <Txt label="References" value={c.brief.references ?? ""} disabled={!canEdit}
              onChange={v => patch({ brief: { ...c.brief, references: v } })} placeholder="AS 2550.1 · client procedure · SWMS no."/>
          </div>
        </div>
      </Card>

      {/* Drawings — direct manipulation. Dragging the pick point or the
          compound rewrites the ACTIVE stage's radii, so reach is checked by
          moving things rather than typing numbers. */}
      <Card>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] uppercase tracking-wider text-text-muted">Plan view</div>
            {canEdit && (
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" icon={Plus}
                  onClick={() => {
                    const n = c.canvas.items.filter(i => i.kind === "pole_section").length + 1;
                    patchCanvas({ items: [...c.canvas.items, newItem("pole_section", n)] });
                  }}>Pole section</Button>
                <Button size="sm" variant="secondary" icon={Plus}
                  onClick={() => {
                    const n = c.canvas.items.filter(i => i.kind === "lattice_module").length + 1;
                    patchCanvas({ items: [...c.canvas.items, newItem("lattice_module", n)] });
                  }}>Lattice module</Button>
                {/* Site furniture — this is what turns the drawing from a crane
                    and a pole into an actual site-specific mud map. */}
                {([
                  ["laydown", "Laydown"], ["hardstand", "Hardstand"], ["platform", "Platform"],
                  ["shelter", "Shelter"], ["access", "Access track"], ["keepout", "Keep clear"],
                ] as const).map(([kind, label]) => (
                  <Button key={kind} size="sm" variant="ghost" icon={Plus}
                    onClick={() => {
                      const n = c.canvas.items.filter(i => i.kind === kind).length + 1;
                      patchCanvas({ items: [...c.canvas.items, newItem(kind, n)] });
                    }}>{label}</Button>
                ))}
              </div>
            )}
          </div>

          {/* Zoom. "Scale (px/m)" as a number field was the only way to zoom,
              which is no way to zoom (James, 01/08). Bigger canvas too — the
              view was cramped before anything was drawn on it. */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] text-text-muted mr-1">Zoom</span>
            <Button size="sm" variant="secondary" disabled={!canEdit}
              onClick={() => patchCanvas({ scale_px_per_m: Math.max(3, Math.round((c.canvas.scale_px_per_m - 1.5) * 10) / 10) })}>−</Button>
            <span className="text-[11px] font-mono text-text-muted w-16 text-center">
              {c.canvas.scale_px_per_m.toFixed(1)} px/m
            </span>
            <Button size="sm" variant="secondary" disabled={!canEdit}
              onClick={() => patchCanvas({ scale_px_per_m: Math.min(40, Math.round((c.canvas.scale_px_per_m + 1.5) * 10) / 10) })}>+</Button>
            <span className="text-[11px] text-text-muted ml-3 mr-1">Crane</span>
            <Button size="sm" variant="secondary" disabled={!canEdit}
              onClick={() => patchCanvas({ crane_heading_deg: ((c.canvas.crane_heading_deg ?? 0) + 345) % 360 })}>↺</Button>
            <Button size="sm" variant="secondary" disabled={!canEdit}
              onClick={() => patchCanvas({ crane_heading_deg: ((c.canvas.crane_heading_deg ?? 0) + 15) % 360 })}>↻</Button>
            <Button size="sm" variant="secondary" disabled={!canEdit}
              onClick={() => patchCanvas({ crane_heading_deg: ((c.canvas.crane_heading_deg ?? 0) + 90) % 360 })}>90°</Button>
            <Button size="sm" variant="ghost" disabled={!canEdit}
              onClick={() => patchCanvas({ pan_x_m: 0, pan_y_m: 0 })}>Recentre</Button>
            <label className="text-[11px] text-accent cursor-pointer ml-2">
              {c.canvas.background_url ? "Replace backdrop" : "Add site backdrop"}
              <input type="file" accept="image/*" className="hidden" disabled={!canEdit}
                onChange={e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  // Downscale to ~1600 px before storing — the drawing lives in
                  // the plan's JSON, so a 6 MB phone photo would bloat every save.
                  const img = new Image();
                  img.onload = () => {
                    const s = Math.min(1, 1600 / img.width);
                    const cv = document.createElement("canvas");
                    cv.width = Math.round(img.width * s); cv.height = Math.round(img.height * s);
                    cv.getContext("2d")?.drawImage(img, 0, 0, cv.width, cv.height);
                    patchCanvas({
                      background_url: cv.toDataURL("image/jpeg", 0.72),
                      bg_scale_px_per_m: c.canvas.bg_scale_px_per_m ?? 20,
                      bg_opacity: c.canvas.bg_opacity ?? 0.45,
                    });
                    URL.revokeObjectURL(img.src);
                  };
                  img.src = URL.createObjectURL(f);
                  e.target.value = "";
                }}/>
            </label>
            {c.canvas.background_url && (
              <Button size="sm" variant="ghost" disabled={!canEdit}
                onClick={() => patchCanvas({ background_url: null })}>Clear backdrop</Button>
            )}
            <Button size="sm" variant="ghost" disabled={!canEdit}
              onClick={() => {
                // Fit: furthest thing from the crane, plus a margin.
                const far = Math.max(
                  radiusOf(c.canvas.compound_x_m, c.canvas.compound_y_m) + c.canvas.compound_m,
                  radiusOf(c.canvas.pick_x_m, c.canvas.pick_y_m),
                  c.canvas.exclusion_r_m,
                  ...c.canvas.items.map(i => radiusOf(i.x_m, i.y_m) + i.length_m / 2),
                  12,
                );
                // Fit to the SHORT side (620/2) not the long one, or the
                // exclusion ring runs off the top and bottom (James, 01/08).
                patchCanvas({ scale_px_per_m: Math.max(3, Math.min(40, Math.round((280 / far) * 10) / 10)) });
              }}>Fit to site</Button>
          </div>
          {/* The canvas shows the ACTIVE stage's pick point, so each lift keeps
              its own pick and moving one can't disturb another. */}
          <LiftPlanPlanView width={980} height={620}
            st={{
              ...c.canvas,
              pick_x_m: activeStage?.pick_x_m ?? c.canvas.pick_x_m,
              pick_y_m: activeStage?.pick_y_m ?? c.canvas.pick_y_m,
            }}
            outW={planConfig?.outrigger_width_m ?? 7.35}
            outL={planConfig?.outrigger_length_m ?? 6.3}
            selectedId={selectedId}
            onSelect={canEdit ? setSelectedId : undefined}
            band={(activeResult?.band ?? "unknown") as any}
            pctChart={activeResult?.pctChart ?? null}
            onChange={canEdit ? next => {
              const i = openStage >= 0 ? openStage : 0;
              const movedPick = next.pick_x_m != null || next.pick_y_m != null;
              // A moved pick is written to THIS stage only — it's a property of
              // the lift, not of the drawing. Everything else (compound, items,
              // structure) is shared and stays on the canvas.
              const { pick_x_m, pick_y_m, ...canvasOnly } = next as any;
              const canvas = { ...c.canvas, ...canvasOnly };
              const stages = [...c.stages];
              // T-436c — any lift bound to a pole section follows that section
              // when it is dragged, so its radius stays honest.
              for (let k = 0; k < stages.length; k++) {
                const bound = stages[k]?.pick_item_id;
                if (!bound) continue;
                const it: any = (canvas.items ?? []).find((x: any) => x.id === bound);
                if (!it) continue;
                stages[k] = { ...stages[k], pick_x_m: it.x_m, pick_y_m: it.y_m,
                  pick_radius_m: Number(radiusOf(it.x_m, it.y_m).toFixed(1)) };
              }
              if (stages[i]) {
                const px = movedPick ? (pick_x_m ?? stages[i].pick_x_m ?? c.canvas.pick_x_m) : (stages[i].pick_x_m ?? c.canvas.pick_x_m);
                const py = movedPick ? (pick_y_m ?? stages[i].pick_y_m ?? c.canvas.pick_y_m) : (stages[i].pick_y_m ?? c.canvas.pick_y_m);
                stages[i] = {
                  ...stages[i],
                  // Hand-dragging the pick marker is an explicit override.
                  ...(movedPick ? { pick_item_id: null } : {}),
                  pick_x_m: px, pick_y_m: py,
                  pick_radius_m: Number(radiusOf(px, py).toFixed(1)),
                  // Radius to the STRUCTURE, not the middle of the pad.
                  place_radius_m: Number(radiusOf(structurePos(canvas).x, structurePos(canvas).y).toFixed(1)),
                };
              }
              patch({ canvas, stages });
            } : undefined}/>

          {/* Compound selected — same control strip as any other object */}
          {selectedId === COMPOUND_ID && canEdit && (() => {
            const rot = (d: number) => patchCanvas({ compound_rotation_deg: (c.canvas.compound_rotation_deg + d + 360) % 360 });
            return (
              <div className="bg-ink rounded-md border border-accent/40 p-3">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-[12px] text-accent flex-1 min-w-[120px]">Site compound</span>
                  <Button size="sm" variant="secondary" onClick={() => rot(-15)}>↺ 15°</Button>
                  <Button size="sm" variant="secondary" onClick={() => rot(15)}>↻ 15°</Button>
                  <Button size="sm" variant="secondary" onClick={() => rot(90)}>90°</Button>
                  <Button size="sm" variant="ghost" onClick={() => rot(-1)}>−1°</Button>
                  <Button size="sm" variant="ghost" onClick={() => rot(1)}>+1°</Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Num label="Compound (m)" value={c.canvas.compound_m} min={4} max={40} step={0.5}
                    onChange={v => patchCanvas({ compound_m: v })}/>
                  <Num label="Rotation (°)" value={c.canvas.compound_rotation_deg} min={0} max={359} step={1}
                    onChange={v => patchCanvas({ compound_rotation_deg: v })}/>
                  <Num label="Pole offset E/W (m)" value={c.canvas.structure_dx_m ?? 0} min={-15} max={15} step={0.1}
                    onChange={v => {
                      const canvas = { ...c.canvas, structure_dx_m: v };
                      const stages = [...c.stages]; const i = openStage >= 0 ? openStage : 0;
                      if (stages[i]) stages[i] = { ...stages[i], place_radius_m: Number(radiusOf(structurePos(canvas).x, structurePos(canvas).y).toFixed(1)) };
                      patch({ canvas, stages });
                    }}/>
                  <Num label="Pole offset N/S (m)" value={c.canvas.structure_dy_m ?? 0} min={-15} max={15} step={0.1}
                    onChange={v => {
                      const canvas = { ...c.canvas, structure_dy_m: v };
                      const stages = [...c.stages]; const i = openStage >= 0 ? openStage : 0;
                      if (stages[i]) stages[i] = { ...stages[i], place_radius_m: Number(radiusOf(structurePos(canvas).x, structurePos(canvas).y).toFixed(1)) };
                      patch({ canvas, stages });
                    }}/>
                  <Num label="℄ label across (m)" value={c.canvas.structure_label_dx_m ?? 0} min={-25} max={25} step={0.5}
                    onChange={v => patchCanvas({ structure_label_dx_m: v })}/>
                  <Num label="℄ label up/down (m)" value={c.canvas.structure_label_dy_m ?? 0} min={-25} max={25} step={0.5}
                    onChange={v => patchCanvas({ structure_label_dy_m: v })}/>
                </div>
                <div className="text-[11px] text-text-dim mt-1.5">
                  Pole offsets are in compound axes, so they rotate with the pad and read straight off the FC setout.
                  The place radius follows the pole, not the middle of the pad.
                </div>
              </div>
            );
          })()}

          {/* Inspector for whatever is selected on the canvas */}
          {selectedId && selectedId !== COMPOUND_ID && canEdit && (() => {
            const it = c.canvas.items.find(x => x.id === selectedId);
            if (!it) return null;
            const upd = (next: Partial<PlacedItem>) => patchCanvas({
              items: c.canvas.items.map(x => x.id === it.id ? { ...x, ...next } : x),
            });
            return (
              // Controls live beside the drawing, not on it. An on-canvas delete
              // badge interfered with dragging (James, 01/08), and the plan view
              // is too cramped to hang buttons off the shapes anyway.
              <div className="bg-ink rounded-md border border-accent/40 p-3">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-[12px] text-accent flex-1 min-w-[120px]">
                    {it.label ?? it.kind.replace("_", " ")}
                  </span>
                  <Button size="sm" variant="secondary"
                    onClick={() => upd({ rot_deg: (it.rot_deg + 345) % 360 })}>↺ 15°</Button>
                  <Button size="sm" variant="secondary"
                    onClick={() => upd({ rot_deg: (it.rot_deg + 15) % 360 })}>↻ 15°</Button>
                  <Button size="sm" variant="secondary"
                    onClick={() => upd({ rot_deg: (it.rot_deg + 90) % 360 })}>90°</Button>
                  {/* Fine trim — the app hides native number spinners, so give
                      explicit single-degree nudges (James, 01/08). */}
                  <Button size="sm" variant="ghost"
                    onClick={() => upd({ rot_deg: (it.rot_deg + 359) % 360 })}>−1°</Button>
                  <Button size="sm" variant="ghost"
                    onClick={() => upd({ rot_deg: (it.rot_deg + 1) % 360 })}>+1°</Button>
                  <Button size="sm" variant="secondary" icon={Plus}
                    onClick={() => {
                      const copy = { ...it, id: `${it.kind}-${Date.now()}`, x_m: it.x_m + 2, y_m: it.y_m + 2 };
                      patchCanvas({ items: [...c.canvas.items, copy] });
                      setSelectedId(copy.id);
                    }}>Duplicate</Button>
                  <Button size="sm" variant="danger" icon={Trash2}
                    onClick={() => { patchCanvas({ items: c.canvas.items.filter(x => x.id !== it.id) }); setSelectedId(null); }}>
                    Delete
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Txt label="Label" value={it.label ?? ""} onChange={v => upd({ label: v })}/>
                  <Num label="Length (m)" value={it.length_m} min={0.5} max={40} step={0.5} onChange={v => upd({ length_m: v })}/>
                  <Num label="Width (m)" value={it.width_m} min={0.2} max={20} step={0.1} onChange={v => upd({ width_m: v })}/>
                  <Num label="Rotation (°)" value={it.rot_deg} min={0} max={359} step={1} onChange={v => upd({ rot_deg: v })}/>
                  <Num label="Label across (m)" value={it.label_dx_m ?? 0} min={-25} max={25} step={0.5}
                    onChange={v => upd({ label_dx_m: v })}/>
                  <Num label="Label up/down (m)" value={it.label_dy_m ?? 0} min={-25} max={25} step={0.5}
                    onChange={v => upd({ label_dy_m: v })}/>
                </div>
                <div className="text-[11px] text-text-dim mt-1.5">
                  If a label ends up buried under something, nudge it clear — a leader line is drawn back to the shape.
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Num label="Compound (m)" value={c.canvas.compound_m} min={4} max={30} step={0.5}
              onChange={v => patchCanvas({ compound_m: v })} disabled={!canEdit}/>
            <Num label="Compound rotation (°)" value={c.canvas.compound_rotation_deg} min={0} max={359} step={5}
              onChange={v => patchCanvas({ compound_rotation_deg: v })} disabled={!canEdit}/>
            <Num label="Slew (°)" value={c.canvas.slew_deg} min={0} max={359} step={5}
              onChange={v => patchCanvas({ slew_deg: v })} disabled={!canEdit}/>
            <Num label="Exclusion zone (m)" value={c.canvas.exclusion_r_m} min={0} max={40} step={0.5}
              onChange={v => patchCanvas({ exclusion_r_m: v })} disabled={!canEdit}/>
            {c.canvas.structure === "lattice" && (
              <Num label="Leg spread (m)" value={c.canvas.leg_spread_m} min={2} max={20} step={0.5}
                onChange={v => patchCanvas({ leg_spread_m: v })} disabled={!canEdit}/>
            )}
            <Num label="Pole offset E/W (m)" value={c.canvas.structure_dx_m ?? 0} min={-15} max={15} step={0.1}
              onChange={v => {
                const canvas = { ...c.canvas, structure_dx_m: v };
                const stages = [...c.stages];
                const i = openStage >= 0 ? openStage : 0;
                if (stages[i]) stages[i] = { ...stages[i], place_radius_m: Number(radiusOf(structurePos(canvas).x, structurePos(canvas).y).toFixed(1)) };
                patch({ canvas, stages });
              }} disabled={!canEdit}/>
            <Num label="Pole offset N/S (m)" value={c.canvas.structure_dy_m ?? 0} min={-15} max={15} step={0.1}
              onChange={v => {
                const canvas = { ...c.canvas, structure_dy_m: v };
                const stages = [...c.stages];
                const i = openStage >= 0 ? openStage : 0;
                if (stages[i]) stages[i] = { ...stages[i], place_radius_m: Number(radiusOf(structurePos(canvas).x, structurePos(canvas).y).toFixed(1)) };
                patch({ canvas, stages });
              }} disabled={!canEdit}/>
            <Num label="Structure height (m)" value={c.canvas.structure_height_m} min={5} max={80} step={1}
              onChange={v => patchCanvas({ structure_height_m: v })} disabled={!canEdit}/>
            <Num label="Ground grade (%)" value={c.canvas.ground_gradient_pct} min={-15} max={15} step={0.5}
              onChange={v => patchCanvas({ ground_gradient_pct: v })} disabled={!canEdit}/>
            <Num label="Scale (px/m)" value={c.canvas.scale_px_per_m} min={3} max={40} step={0.5}
              onChange={v => patchCanvas({ scale_px_per_m: v })} disabled={!canEdit}/>
            <Num label="Crane heading (°)" value={c.canvas.crane_heading_deg ?? 0} min={0} max={359} step={1}
              onChange={v => patchCanvas({ crane_heading_deg: v })} disabled={!canEdit}/>
            {c.canvas.background_url && (
              <>
                <Num label="Backdrop px per metre" value={c.canvas.bg_scale_px_per_m ?? 20} min={1} max={200} step={0.5}
                  onChange={v => patchCanvas({ bg_scale_px_per_m: v })} disabled={!canEdit}/>
                <Num label="Backdrop opacity" value={c.canvas.bg_opacity ?? 0.45} min={0.05} max={1} step={0.05}
                  onChange={v => patchCanvas({ bg_opacity: v })} disabled={!canEdit}/>
                <Num label="Backdrop offset E/W (m)" value={c.canvas.bg_x_m ?? 0} min={-200} max={200} step={0.5}
                  onChange={v => patchCanvas({ bg_x_m: v })} disabled={!canEdit}/>
                <Num label="Backdrop offset N/S (m)" value={c.canvas.bg_y_m ?? 0} min={-200} max={200} step={0.5}
                  onChange={v => patchCanvas({ bg_y_m: v })} disabled={!canEdit}/>
              </>
            )}
          </div>

          <div className="text-[11px] uppercase tracking-wider text-text-muted pt-2">
            Elevation · {activeStage?.name}
          </div>
          {/* Elevation is drawn at the PLACE radius — that's where height is
              needed. Capacity is still judged at the governing radius. */}
          <LiftPlanElevation st={c.canvas}
            boomM={activeStage?.boom_length_m || 0}
            radiusM={activeStage?.place_radius_m || activeResult?.governingRadiusM || 0}
            pieceLengthM={activeStage?.piece_length_m ?? 0}
            builtHeightM={activeStage?.built_height_m ?? 0}
            stageLabel={activeStage?.name}/>
        </div>
      </Card>

      {/* Off-screen elevations, one per stage, purely so the PDF can snapshot
          every lift rather than only whichever stage happened to be open.
          Positioned off-canvas rather than display:none — a hidden SVG has no
          layout box and serialises blank. */}
      <div aria-hidden style={{ position: "absolute", left: -99999, top: 0, width: 1000 }}>
        {/* A plan view per lift too, each showing THAT lift's pick point — the
            PDF was carrying one plan for the whole study, so every lift after
            the first showed the wrong pick radius (James, 03/08). */}
        {c.stages.map((s, i) => (
          <LiftPlanPlanView key={`p${i}`} id={`lp-plan-pdf-${i}`} width={980} height={620}
            st={{
              ...c.canvas,
              pick_x_m: s.pick_x_m ?? c.canvas.pick_x_m,
              pick_y_m: s.pick_y_m ?? c.canvas.pick_y_m,
            }}
            outW={planConfig?.outrigger_width_m ?? 7.35}
            outL={planConfig?.outrigger_length_m ?? 6.3}
            band={(results[i]?.band ?? "unknown") as any}
            pctChart={results[i]?.pctChart ?? null}/>
        ))}
        {c.stages.map((s, i) => (
          // Same 980x620 box as the plan, so the two sit side by side in the PDF
          // at identical size instead of one being squat (James, 03/08).
          <LiftPlanElevation key={i} id={`lp-elev-pdf-${i}`} st={c.canvas} width={980} height={620}
            boomM={s.boom_length_m || 0}
            radiusM={s.place_radius_m || results[i]?.governingRadiusM || 0}
            pieceLengthM={s.piece_length_m ?? 0}
            builtHeightM={s.built_height_m ?? 0}
            stageLabel={s.name}/>
        ))}
      </div>

      {/* Stages */}
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">Stages · {c.stages.length}</div>
        {canEdit && (
          <Button size="sm" variant="secondary" icon={Plus}
            onClick={() => { patch({ stages: [...c.stages, emptyStage(c.stages.length + 1)] }); setOpenStage(c.stages.length); }}>
            Add stage
          </Button>
        )}
      </div>

      {c.stages.map((s, i) => (
        <StageCard key={i} stage={s} result={results[i]} index={i} open={openStage === i}
          configs={configs} points={planPoints} disabled={!canEdit}
          onToggle={() => setOpenStage(openStage === i ? -1 : i)}
          onChange={next => patchStage(i, next)}
          onRemove={c.stages.length > 1 && canEdit ? () => {
            patch({ stages: c.stages.filter((_, x) => x !== i) });
            setOpenStage(0);
          } : undefined}
          // Lifts get added in the order you think of them, not the order they
          // happen — the embedded butt section is Lift 1 even if it's entered
          // last (James, 03/08).
          onMove={canEdit ? (dir: -1 | 1) => {
            const j = i + dir;
            if (j < 0 || j >= c.stages.length) return;
            const stages = [...c.stages];
            [stages[i], stages[j]] = [stages[j], stages[i]];
            patch({ stages });
            setOpenStage(j);
          } : undefined}
          canMoveUp={i > 0} canMoveDown={i < c.stages.length - 1}
          sections={(c.canvas.items ?? [])
            .filter((it: any) => it.kind === "pole_section")
            .map((it: any) => ({ id: it.id, label: it.label || "Section",
                                 radius: radiusOf(it.x_m, it.y_m) }))}
          onPickSection={canEdit ? (itemId: string) => {
            const stages = [...c.stages];
            const it: any = (c.canvas.items ?? []).find((x: any) => x.id === itemId);
            stages[i] = it
              ? { ...stages[i], pick_item_id: itemId, pick_x_m: it.x_m, pick_y_m: it.y_m,
                  pick_radius_m: Number(radiusOf(it.x_m, it.y_m).toFixed(1)) }
              : { ...stages[i], pick_item_id: null };
            patch({ stages });
          } : undefined}/>
      ))}

      {/* Ground bearing */}
      <Card>
        <div className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1">Ground bearing</div>
          <div className="text-[12px] text-text-muted mb-3">
            Outrigger load × 9.81 ÷ pad area. On soft rural pads this bites long before the chart does.
          </div>
          <div className="space-y-2">
            {c.ground.pads.map((p, i) => {
              const kpa = bearingPressureKpa(p.load_t, p.length_m, p.width_m, p.tare_t);
              const v = kpa != null ? bearingVerdict(kpa) : null;
              return (
                <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                  <div className="text-[12px] text-text-muted">{p.leg}</div>
                  <Num label="Load (t)" value={p.load_t} min={0} max={80} step={0.5} disabled={!canEdit}
                    onChange={val => { const pads = [...c.ground.pads]; pads[i] = { ...p, load_t: val }; patch({ ground: { ...c.ground, pads } }); }}/>
                  <Num label="Pad L (m)" value={p.length_m} min={0.2} max={4} step={0.1} disabled={!canEdit}
                    onChange={val => { const pads = [...c.ground.pads]; pads[i] = { ...p, length_m: val }; patch({ ground: { ...c.ground, pads } }); }}/>
                  <Num label="Pad W (m)" value={p.width_m} min={0.2} max={4} step={0.1} disabled={!canEdit}
                    onChange={val => { const pads = [...c.ground.pads]; pads[i] = { ...p, width_m: val }; patch({ ground: { ...c.ground, pads } }); }}/>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-text-dim font-mono">Pressure</div>
                    {kpa == null ? <div className="text-[13px] text-text-dim">—</div> : (
                      <div className={`text-[13px] ${v!.tone === "ok" ? "text-ok" : v!.tone === "warning" ? "text-warn" : "text-danger"}`}>
                        {kpa.toFixed(0)} kPa
                      </div>
                    )}
                  </div>
                  {kpa != null && <div className="col-span-2 md:col-span-5 text-[11px] text-text-muted -mt-1">{v!.label}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Personnel + environment */}
      <Card>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Txt label="Crane operator" value={c.personnel.operator ?? ""} disabled={!canEdit}
            onChange={v => patch({ personnel: { ...c.personnel, operator: v } })}/>
          <Txt label="Dogman" value={c.personnel.dogman ?? ""} disabled={!canEdit}
            onChange={v => patch({ personnel: { ...c.personnel, dogman: v } })}/>
          <Txt label="Riggers" value={c.personnel.riggers ?? ""} disabled={!canEdit}
            onChange={v => patch({ personnel: { ...c.personnel, riggers: v } })}/>
          <Txt label="Lift supervisor" value={c.personnel.supervisor ?? ""} disabled={!canEdit}
            onChange={v => patch({ personnel: { ...c.personnel, supervisor: v } })}/>
          <Txt label="Powerlines / clearances" value={c.environment.powerlines ?? ""} disabled={!canEdit}
            onChange={v => patch({ environment: { ...c.environment, powerlines: v } })}/>
          <Txt label="Public interface" value={c.environment.public_interface ?? ""} disabled={!canEdit}
            onChange={v => patch({ environment: { ...c.environment, public_interface: v } })}/>
          <Txt label="Max wind (m/s)" value={String(c.environment.wind_limit_ms ?? "")} disabled={!canEdit}
            onChange={v => patch({ environment: { ...c.environment, wind_limit_ms: v === "" ? null : Number(v) } })}
            placeholder={planConfig?.chart_wind_limit_ms ? `${planConfig.chart_wind_limit_ms} from the chart` : "9"}/>
          <label className="block text-[12px] text-text-muted md:col-span-2">Additional notes
            <Textarea rows={2} value={c.notes ?? ""} disabled={!canEdit}
              onChange={e => patch({ notes: e.target.value })}
              placeholder="Anything that doesn't fit the sequence or hazard list"/>
          </label>
        </div>
      </Card>

      {/* Sequence + hazards. Hazards were being captured and then silently never
          printed — the PDF had no hazard section at all (James, 01/08). */}
      <Card>
        <div className="p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">
                Lift sequence &amp; hold points · {c.sequence.length}
              </div>
              {canEdit && (
                <Button size="sm" variant="secondary" icon={Plus}
                  onClick={() => patch({ sequence: [...c.sequence, ""] })}>Add step</Button>
              )}
            </div>
            {c.sequence.length === 0 && (
              <div className="text-[12px] text-text-dim italic">
                No steps yet. This is the section a client reads to understand how the lift actually runs.
              </div>
            )}
            <div className="space-y-2">
              {c.sequence.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="font-mono text-[11px] text-accent w-6 pt-2.5">{String(i + 1).padStart(2, "0")}</span>
                  <Input className="flex-1" value={step} disabled={!canEdit}
                    onChange={e => { const s = [...c.sequence]; s[i] = e.target.value; patch({ sequence: s }); }}
                    placeholder="Set up and level the crane on the compacted pad; confirm outrigger pads bearing"/>
                  {canEdit && (
                    <Button size="sm" variant="ghost" icon={Trash2}
                      onClick={() => patch({ sequence: c.sequence.filter((_, j) => j !== i) })}/>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-line/50 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">
                Hazards &amp; controls · {c.hazards.length}
              </div>
              {canEdit && (
                <Button size="sm" variant="secondary" icon={Plus}
                  onClick={() => patch({ hazards: [...c.hazards, { hazard: "", control: "" }] })}>Add hazard</Button>
              )}
            </div>
            {c.hazards.length === 0 && (
              <div className="text-[12px] text-text-dim italic">Nothing recorded — a lift study without controls won't pass a Tier-1 review.</div>
            )}
            <div className="space-y-2">
              {c.hazards.map((h, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Input className="flex-1" value={h.hazard} disabled={!canEdit} placeholder="Hazard"
                    onChange={e => { const x = [...c.hazards]; x[i] = { ...h, hazard: e.target.value }; patch({ hazards: x }); }}/>
                  <Input className="flex-1" value={h.control} disabled={!canEdit} placeholder="Control"
                    onChange={e => { const x = [...c.hazards]; x[i] = { ...h, control: e.target.value }; patch({ hazards: x }); }}/>
                  {canEdit && (
                    <Button size="sm" variant="ghost" icon={Trash2}
                      onClick={() => patch({ hazards: c.hazards.filter((_, j) => j !== i) })}/>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex gap-2 max-w-[1000px] w-full items-center">
          {canEdit && (
            <Button variant="secondary" size="lg" onClick={() => void save()} disabled={busy != null}>
              {busy === "save" ? <Loader2 size={14} className="animate-spin"/>
                : saved ? <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-ok"/> Saved</span>
                : dirty ? "Save draft *" : "Save draft"}
            </Button>
          )}
          {approved ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-[13px] text-ok">
              <CheckCircle2 size={15}/> Approved v{row.version}
            </div>
          ) : isDirector ? (
            <Button size="lg" icon={ShieldCheck} onClick={() => void approve()}
              disabled={busy != null || worst === "over"} className="flex-1 justify-center">
              {busy === "approve" ? <Loader2 size={14} className="animate-spin"/> : `Approve lift plan v${(row.version ?? 0) + 1}`}
            </Button>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[12px] text-text-muted">Director approval releases it.</div>
          )}
        </div>
      </StickyBottomBar>
    </div>
  );
}

// -----------------------------------------------------------------------------
function StageCard({ stage, result, index, open, configs, points, disabled, onToggle, onChange, onRemove,
  onMove, canMoveUp, canMoveDown, sections, onPickSection }: {
  stage: Stage; result: ReturnType<typeof computeStage>; index: number; open: boolean;
  configs: any[]; points: ChartPoint[]; disabled?: boolean;
  onToggle: () => void; onChange: (next: Partial<Stage>) => void; onRemove?: () => void;
  onMove?: (dir: -1 | 1) => void; canMoveUp?: boolean; canMoveDown?: boolean;
  sections?: { id: string; label: string; radius: number }[];
  onPickSection?: (itemId: string) => void;
}) {
  const booms = availableBooms(points, stage.variant);
  // Work the boom out rather than making James guess it — capacity at the
  // governing radius, height at the place radius. Shortest that clears both.
  const options = boomOptions(points, {
    grossKg: result.grossKg,
    governingRadiusM: result.governingRadiusM,
    placeRadiusM: stage.place_radius_m,
    requiredHeightM: (stage.built_height_m ?? 0) + (stage.piece_length_m ?? 0),
    variant: stage.variant,
  });
  const suggested = options.find(o => o.ok) ?? null;
  const tone: Tone = result.band === "ok" ? "ok" : result.band === "caution" ? "warning" : result.band === "over" ? "danger" : "default";
  const pctLabel = result.pctChart == null ? "off chart" : `${result.pctChart.toFixed(0)}% of chart`;

  return (
    <Card>
      <div className="flex items-center">
        {onMove && (
          <div className="flex flex-col pl-2 -space-y-1">
            <button type="button" title="Move this lift earlier" disabled={!canMoveUp}
              onClick={e => { e.stopPropagation(); onMove(-1); }}
              className="text-[11px] leading-none px-1 py-0.5 text-text-muted hover:text-accent disabled:opacity-25 disabled:hover:text-text-muted">▲</button>
            <button type="button" title="Move this lift later" disabled={!canMoveDown}
              onClick={e => { e.stopPropagation(); onMove(1); }}
              className="text-[11px] leading-none px-1 py-0.5 text-text-muted hover:text-accent disabled:opacity-25 disabled:hover:text-text-muted">▼</button>
          </div>
        )}
      <button type="button" onClick={onToggle} className="flex-1 text-left px-3 py-3 flex items-center gap-3 flex-wrap">
        <span className="font-mono text-[11px] text-text-muted w-6">{String(index + 1).padStart(2, "0")}</span>
        <span className="text-[13px] font-medium flex-1 min-w-[160px]">{stage.name}</span>
        <span className="text-[12px] text-text-muted">{fmtKg(result.grossKg)} gross</span>
        <Pill label={pctLabel} tone={tone} small/>
        {open ? <ChevronUp size={14} className="text-text-muted"/> : <ChevronDown size={14} className="text-text-muted"/>}
      </button>
      </div>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-line/50 pt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Txt label="Stage name" value={stage.name} disabled={disabled} onChange={v => onChange({ name: v })}/>
            <Txt label="Piece" value={stage.piece_desc} disabled={disabled} onChange={v => onChange({ piece_desc: v })}
              placeholder="Base section / headframe"/>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Num label="Piece height (m)" value={stage.piece_length_m ?? 0} min={0} max={60} step={0.1}
              disabled={disabled} onChange={v => onChange({ piece_length_m: v })}/>
            <Num label="Already erected (m)" value={stage.built_height_m ?? 0} min={0} max={80} step={0.1}
              disabled={disabled} onChange={v => onChange({ built_height_m: v })}/>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Num label="Piece weight (kg)" value={stage.piece_weight_kg} min={0} max={200000} step={10}
              disabled={disabled} onChange={v => onChange({ piece_weight_kg: v })}/>
            <Num label="Allowances (kg)" value={stage.other_allowance_kg} min={0} max={20000} step={10}
              disabled={disabled} onChange={v => onChange({ other_allowance_kg: v })}/>
            <Num label="Hook block (kg)" value={stage.hook_kg} min={0} max={5000} step={10}
              disabled={disabled} onChange={v => onChange({ hook_kg: v })}/>
            <Num label="Wire rope (kg)" value={stage.rope_kg} min={0} max={5000} step={10}
              disabled={disabled} onChange={v => onChange({ rope_kg: v })}/>
            <label className="block text-[12px] text-text-muted">Boom length (m)
              <Select value={String(stage.boom_length_m || "")} disabled={disabled || booms.length === 0}
                onChange={e => onChange({ boom_length_m: Number(e.target.value) })}>
                <option value="">Pick…</option>
                {options.map(o => (
                  <option key={o.boomM} value={o.boomM}>
                    {o.boomM} m {o.ok ? "✓" : !o.hasCapacity ? "— over capacity" : "— won't reach"}
                  </option>
                ))}
              </Select>
            </label>
            {/* T-436c — pick THIS lift's module, so the radius is the real one
                for the piece being lifted rather than one shared point out at
                the far end of the laydown. */}
            <label className="block text-[12px] text-text-muted">Picks from
              <Select value={stage.pick_item_id ?? ""} disabled={disabled || !onPickSection || !(sections?.length)}
                onChange={e => onPickSection?.(e.target.value)}>
                <option value="">Hand-placed pick point</option>
                {(sections ?? []).map(sec => (
                  <option key={sec.id} value={sec.id}>{sec.label} — {sec.radius.toFixed(1)} m</option>
                ))}
              </Select>
            </label>
            <Num label="Pick radius (m)" value={stage.pick_radius_m} min={1} max={60} step={0.5}
              disabled={disabled || !!stage.pick_item_id}
              onChange={v => onChange({ pick_radius_m: v })}/>
            <Num label="Place radius (m)" value={stage.place_radius_m} min={1} max={60} step={0.5}
              disabled={disabled} onChange={v => onChange({ place_radius_m: v })}/>
            <label className="flex items-center gap-2 text-[12px] text-text-muted mt-5">
              <input type="checkbox" checked={stage.dual_lift} disabled={disabled}
                onChange={e => onChange({ dual_lift: e.target.checked })} className="accent-accent"/>
              Dual lift (+20%)
            </label>
          </div>

          {/* Rigging bill */}
          <div>
            <div className="flex items-baseline justify-between gap-3 mb-1.5 flex-wrap">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">
                Rigging · {fmtKg(result.riggingKg)}
              </div>
              {!(result.riggingKg > 0) && (
                <span className="text-[10px] text-warn">
                  Nothing entered — slings, shackles and clutches are NOT in the lift weight above
                </span>
              )}
            </div>

            {/* T-436c — these were three unlabelled boxes, and the Item field
                collapsed to nothing because the shared Input carries w-full,
                which fights flex-1/w-[70px] passed from here. Size the wrappers
                instead of the input, and say what each column is. */}
            {stage.rigging.length > 0 && (
              <div className="flex gap-2 items-center mb-1 text-[10px] uppercase tracking-wider text-text-dim">
                <div className="flex-1 min-w-[160px]">Item</div>
                <div className="w-[70px]">Qty</div>
                <div className="w-[110px]">Weight each</div>
                <div className="w-[80px] text-right">Total</div>
                {!disabled && <div className="w-[22px]"/>}
              </div>
            )}
            {stage.rigging.map((r, ri) => (
              <div key={ri} className="flex gap-2 items-center mb-1.5">
                <div className="flex-1 min-w-[160px]">
                  <Input value={r.item} disabled={disabled} placeholder="e.g. Double leg chain set 16mm G100"
                    onChange={e => { const g = [...stage.rigging]; g[ri] = { ...r, item: e.target.value }; onChange({ rigging: g }); }}/>
                </div>
                <div className="w-[70px]">
                  <Input value={String(r.qty)} disabled={disabled} placeholder="1"
                    onChange={e => { const g = [...stage.rigging]; g[ri] = { ...r, qty: Number(e.target.value) || 0 }; onChange({ rigging: g }); }}/>
                </div>
                <div className="w-[110px]">
                  <Input value={String(r.unit_kg)} disabled={disabled} placeholder="kg"
                    onChange={e => { const g = [...stage.rigging]; g[ri] = { ...r, unit_kg: Number(e.target.value) || 0 }; onChange({ rigging: g }); }}/>
                </div>
                <div className="w-[80px] text-right font-mono text-[12px] text-text-muted">
                  {fmtKg((Number(r.qty) || 0) * (Number(r.unit_kg) || 0))}
                </div>
                {!disabled && (
                  <button type="button" title="Remove this item"
                    onClick={() => onChange({ rigging: stage.rigging.filter((_, x) => x !== ri) })}
                    className="text-text-muted hover:text-danger p-1"><Trash2 size={12}/></button>
                )}
              </div>
            ))}
            <div className="text-[10px] text-text-dim mt-0.5 mb-1">
              Everything hanging below the hook: slings, shackles, lifting clutches, spreader or strongback, tag hardware.
              The hook block and wire rope are entered separately above.
            </div>
            {!disabled && (
              <button type="button" className="text-[11px] text-accent hover:underline"
                onClick={() => onChange({ rigging: [...stage.rigging, { item: "", qty: 1, unit_kg: 0 }] })}>
                + Add rigging item
              </button>
            )}
          </div>

          {/* The answer, not a quiz — what boom does this lift need? */}
          {options.length > 0 && result.grossKg > 0 && (
            suggested ? (
              <div className={`rounded-md border px-3 py-2.5 ${stage.boom_length_m === suggested.boomM
                ? "bg-ok/10 border-ok/40" : "bg-accent/8 border-accent/40"}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-[12px]">
                    {stage.boom_length_m === suggested.boomM ? <span className="text-ok">✓ </span> : null}
                    Needs <b>{suggested.boomM} m boom</b> — {suggested.pctChart?.toFixed(0)}% of chart at{" "}
                    {result.governingRadiusM.toFixed(1)} m, hook reaches {suggested.hookHeightM?.toFixed(1)} m
                    {(stage.built_height_m || stage.piece_length_m)
                      ? ` for a ${((stage.built_height_m ?? 0) + (stage.piece_length_m ?? 0)).toFixed(1)} m top`
                      : ""}.
                  </div>
                  {!disabled && stage.boom_length_m !== suggested.boomM && (
                    <Button size="sm" onClick={() => onChange({ boom_length_m: suggested.boomM })}>Use it</Button>
                  )}
                </div>
                <div className="text-[11px] text-text-muted mt-1">
                  Shortest boom that carries the load at the stick-out AND clears the landing.
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2.5 text-[12px] text-danger">
                <AlertTriangle size={12} className="inline mr-1.5 -mt-0.5"/>
                <b>No boom on this crane does this lift.</b>{" "}
                {options.every(o => !o.hasCapacity)
                  ? "Every length is over capacity at that radius — shorten the radius or step up a crane class."
                  : "Long enough booms lose the capacity; short enough ones won't reach the height. Needs a bigger crane or a fly."}
              </div>
            )
          )}

          {/* Verdict */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Gross load" value={fmtKg(result.grossKg)}/>
            <Stat label="Governing radius" value={`${result.governingRadiusM.toFixed(1)} m`}/>
            <Stat label="Chart capacity" value={result.chartKg == null ? "off chart" : fmtKg(result.chartKg)}/>
            <Stat label="Utilisation" value={result.pctChart == null ? "—" : `${result.pctChart.toFixed(0)}%`} tone={tone}/>
            {result.pctWinch != null && <Stat label="Winch" value={`${result.pctWinch.toFixed(0)}%`}/>}
            {result.hookHeightM != null && <Stat label="Hook height" value={`${result.hookHeightM.toFixed(1)} m`}/>}
          </div>

          {stage.boom_length_m > 0 && result.grossKg > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-text-muted mb-1">
                Utilisation across the reach · {stage.boom_length_m} m boom
              </div>
              <CapacityCurve points={points} boomM={stage.boom_length_m} variant={stage.variant}
                grossKg={result.grossKg} currentRadiusM={result.governingRadiusM}/>
            </div>
          )}

          {result.warnings.length > 0 && (
            <div className="bg-warn/10 border border-warn/40 rounded-md px-3 py-2 space-y-1">
              {result.warnings.map((w, i) => (
                <div key={i} className="text-[12px] text-warn flex items-start gap-2">
                  <AlertTriangle size={12} className="mt-0.5 flex-shrink-0"/>{w}
                </div>
              ))}
            </div>
          )}

          {onRemove && (
            <button type="button" onClick={onRemove} className="text-[11px] text-text-muted hover:text-danger">
              Remove this stage
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

function Num({ label, value, onChange, min, max, step, disabled }: {
  label: string; value: number; onChange: (v: number) => void;
  min?: number; max?: number; step?: number; disabled?: boolean;
}) {
  return (
    <label className="block text-[12px] text-text-muted">{label}
      <Input type="number" value={String(value ?? 0)} min={min} max={max} step={step} disabled={disabled}
        onChange={e => onChange(Number(e.target.value))}/>
    </label>
  );
}

function Txt({ label, value, onChange, placeholder, disabled }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean;
}) {
  return (
    <label className="block text-[12px] text-text-muted">{label}
      <Input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}/>
    </label>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: Tone }) {
  const cls = tone === "ok" ? "text-ok" : tone === "warning" ? "text-warn" : tone === "danger" ? "text-danger" : "text-text";
  return (
    <div className="bg-ink rounded-md border border-line/50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-text-dim font-mono">{label}</div>
      <div className={`text-[15px] mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}
